import { Theme } from '@core/enums';
import animation, { fadeInUp } from '@helpers/animation';
import brand from '@helpers/brand';
import { legacyStylesOverride } from '@helpers/global';
import { between, Device, from } from '@helpers/media';
import { ButtonStyles } from '@stories/Elements/Forms/Buttons/Button.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import styled, { css } from 'styled-components';

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding-bottom: 61.3%;
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  min-height: 200px;
  transform: scale(1.12) translateX(10px);
  will-change: transform filter;
  transition: transform 300ms ${animation.easeInOutCubic}, filter 300ms ${animation.easeInOutCubic};
`;

const Icon = styled.img`
  position: absolute;
  z-index: 10;
  top: 0;
  transform: translateY(-50%);
  pointer-events: none;
  display: block;
  max-height: 44px;
  max-width: 40%;

  @media ${from(Device.TabletLarge)} {
    max-height: 91px;
    max-width: 100%;
  }
`;

const Text = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 42px 12px 28px;
  text-align: center;
  max-width: 100%;

  @media ${from(Device.TabletLarge)} {
    padding: 62px 18px 28px;
  }

  h3 {
    display: flex;
    align-items: center;
    flex: 1;
    margin: 0;
    color: ${brand.white};
    ${Typography.H5Style};
    max-width: 100%;
    font-size: 14px;

    @media ${between(Device.Tablet, Device.TabletLarge)} {
      font-size: 17px;
    }

    ${legacyStylesOverride(`
      color: ${brand.white};
      ${Typography.H5Style};
    `)}
  }
`;

const Card = styled.a<{ inView: boolean; delay: number; theme: Theme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;

  ${({ theme }) => {
    switch (theme) {
      default:
      case Theme.Red: {
        return css`
          background-color: ${brand.red.base};
        `;
      }

      case Theme.White: {
        return css`
          background-color: ${brand.white};

          ${Text} h3 {
            color: ${brand.black};

            ${legacyStylesOverride(`
              color: ${brand.black};
            `)}
          }
        `;
      }

      case Theme.Black: {
        return css`
          background-color: ${brand.black};
        `;
      }
    }
  }};

  ${({ inView, delay }) => (inView !== undefined ? fadeInUp(inView, 25, 500, delay, animation.easeInOutCubic) : null)};

  &:hover {
    ${ImageWrapper} {
      &:before {
        transform: scaleX(1);
      }

      &:after {
        opacity: 1;
        transform: scale(1);
      }
    }

    ${Image} {
      transform: scale(1.12) translateX(-10px);
      filter: grayscale(0.75) contrast(1.1);
    }
  }

  ${ButtonStyles.ButtonElement} {
    margin-top: 18px;
    font-size: 14px;

    @media ${from(Device.TabletLarge)} {
      font-size: 16px;
    }

    @media ${from(Device.Desktop)} {
      font-size: 20px;
    }
  }
`;

const ButtonWrapper = styled.div`
  margin-top: auto;
`;

export const SystemCardStyles = {
  Card,
  ImageWrapper,
  Image,
  Icon,
  Text,
  ButtonWrapper,
};
